import { useState } from 'react';

import type { FeedbackState, MCQFeedback , GradingMode } from '@agavi/blocks';

/** Whether a feedback body (rich-text HTML) is blank once tags are stripped — treats "<p></p>" etc. as empty. */
export function isFeedbackContentEmpty(content: string): boolean {
  return !content.replace(/<[^>]+>/g, '').trim();
}

/** Whether any feedback state in the block has blank body text — surfaced as a warning on the collapsed answer bar. */
export function hasEmptyFeedbackContent(states: FeedbackState[]): boolean {
  return states.some(s => isFeedbackContentEmpty(s.content));
}

/** For "clone with smart reset" scenarios (e.g. seeding a new dropdown branch's feedback from an
 * existing one): content that still matches its slot's known default is untouched boilerplate,
 * safe to carry forward as-is — content that's already blank stays blank — but content that's
 * been customized was written for a specific context and likely doesn't apply somewhere else, so
 * it resets to blank rather than silently carrying over. Blank, not the default text, is
 * deliberate: default text is non-empty, so a card showing it starts collapsed and never trips the
 * existing missing-feedback-text warning — a teacher could easily never notice it silently
 * reverted to generic wording. Blank surfaces the change immediately (expanded card + standing
 * warning), the same nudge that already exists for "you haven't written this yet." */
export function resetContentIfCustomized(content: string, defaultContent: string): string {
  return content === defaultContent || isFeedbackContentEmpty(content) ? content : '';
}

export interface FeedbackDefaults {
  unansweredTitle: string;
  unansweredContent: string;
  incorrectTitle: string;
  incorrectContent: string;
  correctTitle: string;
  correctContent: string;
}

/** A block-specific fixed default state beyond the universal three (e.g. numerical's partial_answer, sigfig_error). */
export interface ExtraDefaultState {
  trigger: string;
  title: string;
  content: string;
  evaluation: FeedbackState['evaluation'];
}

export interface FeedbackTitleLabels {
  unanswered: string;
  correct: string;
  incorrect: string;
  /** Locked title labels for extra default states, keyed by trigger. */
  extra?: Record<string, string>;
}

/** Re-exported for the same reason `grading-modes.ts` does — see its note. */
export type { GradingMode } from '@agavi/blocks';
/**
 * The "positive slot" trigger name for a given mode — the one state that's always present and
 * fires once a complete, valid/accepted answer exists. Every migrated block uses one of these
 * three literal names instead of reusing a single `correct_any` across modes with different
 * meanings each time (graded "matched the correct answer" vs. survey "answered at all, no
 * correctness involved" vs. optional "any response, including blank, is accepted") — a
 * misleading pattern the whole shared evaluation template originally baked in, confirmed present
 * in all 11 gradable blocks. Naming the trigger itself per-mode keeps the raw string
 * self-describing for anyone reading stored attempt data later, without needing the block's
 * gradingMode alongside it for context.
 */
export function positiveTriggerForMode(mode: GradingMode): 'correct' | 'any_answer' | 'any_response' {
  return mode === 'graded' ? 'correct' : mode === 'survey' ? 'any_answer' : 'any_response';
}

/** The positive slot's name, one per grading mode — see positiveTriggerForMode. */
const POSITIVE_TRIGGERS: readonly string[] = ['correct', 'any_answer', 'any_response'];

/** Module level so the normalizer and the title lookup ask the same question of the same list —
 * they used to each spell the names out, which is how a retired one survived in only one of them. */
function isPositiveTriggerName(trigger: string): boolean {
  return POSITIVE_TRIGGERS.includes(trigger);
}

/**
 * The retired names, renamed once on the way in and never spoken of again.
 *
 * `correct_any` and `incorrect_any` predate the per-mode trigger names. Nothing writes them and the
 * stored data was migrated, so they are gone from the model — not in POSITIVE_TRIGGERS, not in any
 * matcher, not in the title lookup. This one-way rename is what makes that safe: without it, a state
 * under an old name matches no branch below, so it is not misfiled but DISCARDED, and the fresh
 * default that replaces it takes the teacher's own Correct text with it, silently. Cheap insurance
 * against the one route that can still produce old data — a database restored from an older backup.
 *
 * `correct_any` is not Participation, which is `any_answer`; the name misleads, which is part of why
 * it is retired.
 */
function renameRetiredTriggers(states: FeedbackState[], positiveTrigger: string): FeedbackState[] {
  return states.map(s => {
    if (s.type === 'targeted') return s;
    if (s.trigger === 'correct_any') return { ...s, trigger: positiveTrigger };
    if (s.trigger === 'incorrect_any') return { ...s, trigger: 'incorrect' };
    return s;
  });
}

export interface PositiveSlotContent {
  correct: string;
  anyAnswer: string;
  anyResponse: string;
}

/**
 * Call from a block's own handleGradingMode when the grading mode changes — rewrites the
 * positive-slot state's trigger (and its locked evaluation) to match the new mode, and swaps its
 * body to the new mode's default ONLY when it still holds one of the three known defaults
 * untouched, so a teacher's own custom wording is never clobbered by a mode switch. All three
 * positive-slot triggers (`correct`/`any_answer`/`any_response`) lock to evaluation 'correct' —
 * any response at all IS the correct answer in Optional mode, so it gets the same green
 * "you're doing fine" treatment as the other two, it just stops calling itself `correct_any` in
 * the data.
 */
export function retriggerPositiveSlot(states: FeedbackState[], newMode: GradingMode, content: PositiveSlotContent): FeedbackState[] {
  const newTrigger = positiveTriggerForMode(newMode);
  const knownDefaults = new Set([content.correct, content.anyAnswer, content.anyResponse]);
  const newEvaluation: FeedbackState['evaluation'] = 'correct';
  const newContent = newTrigger === 'correct' ? content.correct : newTrigger === 'any_answer' ? content.anyAnswer : content.anyResponse;
  return states.map(s => {
    if (s.type !== 'default' || !POSITIVE_TRIGGERS.includes(s.trigger)) return s;
    if (s.trigger === newTrigger) return s;
    return { ...s, trigger: newTrigger, evaluation: newEvaluation, content: knownDefaults.has(s.content) ? newContent : s.content };
  });
}

/**
 * `positiveTrigger` is `positiveTriggerForMode(gradingMode)` and is REQUIRED — all 11 blocks pass
 * it, so the old "unmigrated block" path it used to guard was unreachable.
 *
 * A stored state under ANY of the historical positive-slot names is recognized as the same slot and
 * then rewritten to the current one, so a name can never linger long enough to be read as a
 * different thing. That rewrite is not cosmetic: every test-bar legend finds its chips by exact
 * trigger name, so a block left under an old name rendered with a whole category missing.
 */
/**
 * The default feedback set every input block starts with, in one place.
 *
 * Each block used to spell this out twice: once in its editor's `useInputBlockFeedback` config, and
 * once (if at all) in a `buildNormalized*Data` for the non-editing render paths. Three blocks had
 * the second copy; eight did not, and the consequence was visible on every freshly added block —
 * the editor seeds these states into its own React state, but nothing is written to `block.data`
 * until the teacher changes something, so the card's test legend read an empty `feedback.states`
 * and printed "No feedback options available yet" beside a feedback panel listing No Answer,
 * Correct and Default Incorrect.
 *
 * Only three things vary across the eleven blocks, and each is an argument here rather than a
 * reason to write the whole set out again: sensors say "no measurement" where the rest say "no
 * answer", Ordering names its catch-all after the thing it catches, and several blocks add fixed
 * states of their own.
 */
export function standardFeedbackDefaults(
  t: (key: string, opts?: Record<string, unknown>) => string,
  gradingMode: GradingMode,
  over: Partial<FeedbackDefaults> = {},
): FeedbackDefaults {
  return {
    unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
    unansweredContent: `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>`,
    incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
    incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
    correctTitle: gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle')
      : gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle')
      : t('blocks.feedbackDefaults.anyResponseTitle'),
    correctContent: gradingMode === 'graded' ? `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`
      : gradingMode === 'survey' ? `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`
      : `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
    ...over,
  };
}

/**
 * `normalizeFeedback` over the standard defaults — what a block's non-editing render paths (the
 * collapsed card's test legend and the student-facing Preview) should read instead of `block.data`
 * raw, so an unsaved default set still shows up as the state it is.
 */
export function normalizeStandardFeedback(
  raw: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
  gradingMode: GradingMode,
  over: { defaults?: Partial<FeedbackDefaults>; extraDefaults?: ExtraDefaultState[] } = {},
): MCQFeedback {
  return normalizeFeedback(
    raw,
    standardFeedbackDefaults(t, gradingMode, over.defaults),
    over.extraDefaults ?? [],
    positiveTriggerForMode(gradingMode),
  );
}

export function normalizeFeedback(raw: unknown, defaults: FeedbackDefaults, extraDefaults: ExtraDefaultState[], positiveTrigger: string): MCQFeedback {
  const r = raw as { states?: FeedbackState[] } | undefined;
  let unanswered: FeedbackState | undefined;
  let incorrect: FeedbackState | undefined;
  let correct: FeedbackState | undefined;
  const extraFound = new Map<string, FeedbackState>();
  const targeted: FeedbackState[] = [];
  const extraTriggers = new Set(extraDefaults.map(e => e.trigger));

  if (Array.isArray(r?.states)) {
    for (const s of renameRetiredTriggers(r.states as FeedbackState[], positiveTrigger)) {
      // Backfill missing type field from legacy data
      const isKnownDefaultTrigger = s.trigger === 'unanswered' || s.trigger === 'incorrect' || isPositiveTriggerName(s.trigger) || extraTriggers.has(s.trigger);
      const typed: FeedbackState = s.type ? s : { ...s, type: isKnownDefaultTrigger ? 'default' : 'targeted' };
      if (typed.type === 'default') {
        if (typed.trigger === 'unanswered') unanswered = typed;
        else if (typed.trigger === 'incorrect') incorrect = typed;
        else if (isPositiveTriggerName(typed.trigger)) correct = typed;
        else if (extraTriggers.has(typed.trigger)) extraFound.set(typed.trigger, typed);
      } else {
        targeted.push(typed);
      }
    }
  }

  /*
   * `?? ''`, because a stored state may have no `content` at all.
   *
   * `FeedbackState` types it as required and the editor always writes one, but block `data` is
   * an untyped object on the way in and the database holds states without it. Reading `.trim()`
   * off the missing field threw during `useState`'s initializer, which is not a blank card but
   * a dead one: the whole block failed to mount, in the editor AND in Preview. Found by
   * rendering every stored block through its own component.
   */
  if (incorrect && !(incorrect.content ?? '').trim()) incorrect = { ...incorrect, content: defaults.incorrectContent };
  if (correct && !(correct.content ?? '').trim()) correct = { ...correct, content: defaults.correctContent };
  // Self-heals data saved before any_response was corrected to evaluation 'correct' — any
  // response at all IS the correct answer in Optional mode, so it should never load back in as
  // neutral just because that's how an older save happened to store it.
  if (correct && correct.evaluation !== 'correct') correct = { ...correct, evaluation: 'correct' };
  // Canonicalises a positive slot still stored under an older trigger name — `correct_any` from
  // before the per-mode triggers landed, or a name left over from a mode the block is no longer in.
  // `retriggerPositiveSlot` already does this when the teacher CHANGES mode; doing it on load too
  // means a block that has simply never been re-moded stops reading as if it had no positive state
  // at all. That was not theoretical: every test-bar legend finds its correct chips with
  // `trigger === 'correct'`, so 13 stored blocks across 8 types were rendering with the entire
  // CORRECT category silently missing. Fixing it here rather than at the six read sites keeps the
  // one canonical name a real invariant instead of something each caller re-derives.
  if (correct && correct.trigger !== positiveTrigger) correct = { ...correct, trigger: positiveTrigger };
  if (incorrect && incorrect.trigger !== 'incorrect') incorrect = { ...incorrect, trigger: 'incorrect' };

  // Ids derived from the trigger, NOT minted. `normalizeFeedback` runs during render — via each
  // block's `buildNormalized*Data` — so a block whose feedback has never been saved got a fresh id
  // for each of these default states on every render, and a state's id is what `firedIds`, the
  // test-bar chips, Flow rules and targeted triggers all match on. Measured before the fix: three
  // ids minted per normalize call, twice per click. A trigger is unique among a block's default
  // states, and the `default-` prefix cannot collide with a targeted state's minted UUID.
  if (!unanswered) unanswered = { id: 'default-unanswered', type: 'default', trigger: 'unanswered', title: defaults.unansweredTitle, content: defaults.unansweredContent, evaluation: 'neutral' };
  if (!incorrect) incorrect = { id: 'default-incorrect', type: 'default', trigger: 'incorrect', title: defaults.incorrectTitle, content: defaults.incorrectContent, evaluation: 'incorrect' };
  if (!correct) correct = { id: `default-${positiveTrigger}`, type: 'default', trigger: positiveTrigger, title: defaults.correctTitle, content: defaults.correctContent, evaluation: 'correct' };

  const extras = extraDefaults.map(e => {
    const existing = extraFound.get(e.trigger);
    if (!existing) return { id: `default-${e.trigger}`, type: 'default' as const, trigger: e.trigger, title: e.title, content: e.content, evaluation: e.evaluation };
    /* Same reason as the pair above: a stored state may carry no `content`. */
    return (existing.content ?? '').trim() ? existing : { ...existing, content: e.content };
  });

  return { states: [unanswered, ...extras, ...targeted, correct, incorrect] };
}

interface UseInputBlockFeedbackOptions {
  initialRaw: unknown;
  defaults: FeedbackDefaults;
  titleLabels: FeedbackTitleLabels;
  extraDefaults?: ExtraDefaultState[];
  /** `positiveTriggerForMode(gradingMode)`, re-derived fresh on every render so it always reflects
   * the block's current mode. Required: all 11 blocks are migrated. */
  positiveTrigger: string;
  onUpdate: (feedback: MCQFeedback) => void;
}

export function useInputBlockFeedback({ initialRaw, defaults, titleLabels, extraDefaults, positiveTrigger, onUpdate }: UseInputBlockFeedbackOptions) {
  const [feedback, setFeedback] = useState<MCQFeedback>(() => normalizeFeedback(initialRaw, defaults, extraDefaults ?? [], positiveTrigger));

  function getTitle(s: FeedbackState): string {
    if (s.type === 'default') {
      if (s.trigger === 'unanswered') return titleLabels.unanswered;
      if (isPositiveTriggerName(s.trigger)) return titleLabels.correct;
      if (s.trigger === 'incorrect') return titleLabels.incorrect;
      if (titleLabels.extra?.[s.trigger]) return titleLabels.extra[s.trigger]!;
    }
    return s.title;
  }


  function updateFeedbackState(id: string, patch: Partial<FeedbackState>) {
    const next = { states: feedback.states.map(s => s.id === id ? { ...s, ...patch } : s) };
    setFeedback(next);
    onUpdate(next);
  }

  function deleteFeedbackState(id: string) {
    const state = feedback.states.find(s => s.id === id);
    if (!state || state.type === 'default') return;
    const next = { states: feedback.states.filter(s => s.id !== id) };
    setFeedback(next);
    onUpdate(next);
  }

  /**
   * Take the feedback from stored data again, normalised the same way it was at mount.
   *
   * For a block that re-reads `block.data` when an undo changes it underneath, instead of being
   * remounted to pick it up. Feedback is the half of a block's state this hook owns, so it is the
   * half the block cannot re-derive for itself — and every block that uses this hook will need it.
   *
   * A no-op when the normalised result is what the hook already holds. The caller cannot tell an
   * undo from an echo of its own write, so it re-syncs on both; without this guard the echo hands
   * every feedback editor a fresh array, and a rich-text editor being typed into is rebuilt from
   * under the cursor for a change that was not one.
   */
  function resyncFeedback(raw: unknown) {
    const next = normalizeFeedback(raw, defaults, extraDefaults ?? [], positiveTrigger);
    if (JSON.stringify(next) === JSON.stringify(feedback)) return;
    setFeedback(next);
  }

  return { feedback, setFeedback, resyncFeedback, updateFeedbackState, deleteFeedbackState, getTitle };
}
